import omit from 'lodash/omit';
import { APP_NAME, MAX_NESTING_LEVEL, ETRConstants, SheetExportTypes } from '../xlsxExport.constants';

import metadataBuilder from './metadataBuilder';
import etrStyleBuilder from './styleBuilders/etrStyleBuilder';
import { ExcelRow } from '@common/xlsxExport/types';

const {
  SortOrders,
  Sections,
} = ETRConstants;


const generateEmptyCells = (count: number) => Array(count).fill('');

type GenerateHeaderRowParams = {
    columnHeaders: string[],
    totalColumnName?: string,
}
const generateHeaderRow = ({ columnHeaders, totalColumnName = 'Total' }: GenerateHeaderRowParams) => {
  const padding = generateEmptyCells(MAX_NESTING_LEVEL);
  const totalCell = totalColumnName;
  const percentageCell = 'Percentage';

  return [...padding, totalCell, percentageCell, ...columnHeaders];
};

type EtrRow = {
  amount: number,
  sortOrder: number,
  columnsData?: (string|number|null)[],
  descr?: string
  subrows?: EtrRow[],
  section?: string,
  // [key: string]: unknown,
}

type GenerateRowParams = {
    row: EtrRow,
    nestingLevel?: number,
    maxNestingLevel?: number,
    includeTotal?: boolean,
    includePercentage?: boolean,
    divideByIbtAmount?: (amount: number) => ExcelRow[number],
    totalValue?: number,
    percentageValue?: number,
    aggregationField: keyof EtrRow,
}
const generateRow = ({
  row,
  nestingLevel = 1,
  maxNestingLevel = MAX_NESTING_LEVEL,
  includeTotal = true,
  includePercentage = true,
  divideByIbtAmount = () => '',
  totalValue,
  percentageValue,
  aggregationField,
}: GenerateRowParams): ExcelRow => {
  const description = row[aggregationField];
  const emptyCellsToTheLeftCount = nestingLevel;
  const emptyCellsToTheRightCount = maxNestingLevel - nestingLevel - 1;

  const columnCells = row.columnsData || [];

  const emptyCellsToTheLeft = generateEmptyCells(emptyCellsToTheLeftCount);
  const emptyCellsToTheRight = generateEmptyCells(emptyCellsToTheRightCount);

  const totalCell = includeTotal ? totalValue || row.amount || '' : '';

  const percentageCell = includePercentage
    ? percentageValue || divideByIbtAmount(row.amount)
    : '';

  const topRow = [
    ...emptyCellsToTheLeft,
    description,
    ...emptyCellsToTheRight,
    totalCell,
    percentageCell,
    ...columnCells,
  ];

  return topRow;
};

type GenerateRowsParams = {
  beginningRows: EtrRow[],
  divideByIbtAmount?: (amount: number) => ExcelRow[number]
  rowsAbove: number,
}
const generateBeginningRows = ({ beginningRows, divideByIbtAmount, rowsAbove }: GenerateRowsParams) => {
  const nonCollapsedRowsIndexes: number[] = [];
  const outputBeginningRows = beginningRows.map((beginningRow, rowIndex) => {
    const shouldNotCollapse
      = beginningRow.sortOrder === SortOrders.INCOME_BEFORE_TAX
      || beginningRow.sortOrder === SortOrders.TAX_RATE;

    if (shouldNotCollapse) {
      nonCollapsedRowsIndexes.push(rowIndex + rowsAbove);
    }

    if (beginningRow.sortOrder === SortOrders.TAX_RATE) {
      const rowWithoutAdditionalData = omit(beginningRow, 'columnsData');

      return generateRow({
        row: rowWithoutAdditionalData as EtrRow,
        divideByIbtAmount,
        nestingLevel: 2,
        percentageValue: beginningRow.amount,
        includeTotal: false,
        aggregationField: 'descr',
      });
    }

    return generateRow({
      row: beginningRow,
      divideByIbtAmount,
      nestingLevel: 0,
      includePercentage: false,
      aggregationField: 'descr',
    });
  });

  return {
    beginningRows: outputBeginningRows,
    nonCollapsedRowsIndexes,
  };
};

type GenerateTagsGroupParams = {
    tagsGroups?: EtrRow[],
    divideByIbtAmount?: (amount: number) => string | number,
    aggregationFields?: (keyof EtrRow)[],
    nestingLevel?: number,
    maxNestingLevel?: number,

}
export const generateTagsGroup = ({
  tagsGroups,
  divideByIbtAmount,
  aggregationFields = [],
  nestingLevel = 1,
  maxNestingLevel = MAX_NESTING_LEVEL,
}: GenerateTagsGroupParams): ExcelRow[] => {
  if (!tagsGroups || aggregationFields.length === 0) {
    return [];
  }

  const [aggregationField, ...remainingAggregationFields] = aggregationFields;

  const rows = tagsGroups.reduce<ExcelRow[]>((acc, tagGroup) => {
    const row = generateRow({
      row: tagGroup,
      divideByIbtAmount,
      nestingLevel,
      maxNestingLevel,
      aggregationField,
    });

    const cells = generateTagsGroup({
      tagsGroups: tagGroup.subrows,
      divideByIbtAmount,
      nestingLevel: nestingLevel + 1,
      maxNestingLevel,
      aggregationFields: remainingAggregationFields,
    });

    return [...acc, row, ...cells];
  }, []);

  return rows;
};

type GenerateSummaryRowsParams = {
    summaryRows: EtrRow[],
    rowsAbove: number,
}
const generateSummaryRows = ({ summaryRows, rowsAbove }: GenerateSummaryRowsParams) => {
  const totalRowsIndexes: number[] = [];
  const percentageRowsIndexes: number[] = [];
  const outputSummaryRows = summaryRows.map((row, index) => {
    if (row.section === Sections.PROVISION_FOR_INCOME_TAXES) {
      totalRowsIndexes.push(index + rowsAbove);

      return generateRow({
        row,
        includePercentage: false,
        totalValue: row.amount,
        aggregationField: 'descr',
      });
    }

    percentageRowsIndexes.push(index + rowsAbove);

    return generateRow({
      row,
      includeTotal: false,
      percentageValue: row.amount,
      aggregationField: 'descr',
    });
  });

  return {
    summaryRows: outputSummaryRows,
    totalRowsIndexes,
    percentageRowsIndexes,
  };
};

export type EtrBuilderSheet = {
  type: SheetExportTypes.ETR_REPORT,

  name: string,

  data: {
    columnHeaders: string[],
    beginningRows: EtrRow[],
    tagsGroups: EtrRow[],
    summaryRows: EtrRow[],
  },
  options: {
    totalColumnName?: string,
    numberOfColumnsToStick: number,
    numberOfAdditionalRowsToStick: number,
    hideDecimals?: boolean,
    negativeMinus?: boolean,
    shouldDisplayZero?: boolean,
    addTimestamp?: boolean,
    aggregationFields: (keyof EtrRow)[],
  }

}

export type EtrBuilderOptions = {
  description?: string,
}

const etrBuilder = (sheet: EtrBuilderSheet, options: EtrBuilderOptions = {}) => {
  const { description } = options;
  const { data, options: sheetOptions } = sheet;

  const {
    totalColumnName,
    numberOfColumnsToStick,
    numberOfAdditionalRowsToStick,
    hideDecimals,
    negativeMinus,
    shouldDisplayZero,
    addTimestamp,
    aggregationFields,
  } = sheetOptions;

  const sheetMetadata = metadataBuilder({
    appName: APP_NAME,
    sheetTitle: sheet.name,
    sheetDescription: description,
    addTimestamp,
  });

  const ibtRow = data.beginningRows.find(
    row => row.sortOrder === SortOrders.INCOME_BEFORE_TAX
  );
  const ibtAmount = ibtRow ? ibtRow.amount : 0;

  const divideByIbtAmount = (value: number) => (ibtAmount ? value / ibtAmount : 0);

  const headerRow = generateHeaderRow({
    columnHeaders: data.columnHeaders,
    totalColumnName,
  });

  const HEADER_ROW_LENGTH = 1;
  const { beginningRows, nonCollapsedRowsIndexes } = generateBeginningRows({
    beginningRows: data.beginningRows,
    divideByIbtAmount,
    rowsAbove: sheetMetadata.length + HEADER_ROW_LENGTH,
  });

  const tagGroupRows = generateTagsGroup({
    tagsGroups: data.tagsGroups,
    divideByIbtAmount,
    aggregationFields,
  });

  const { summaryRows, totalRowsIndexes, percentageRowsIndexes } = generateSummaryRows({
    summaryRows: data.summaryRows,
    rowsAbove: sheetMetadata.length
      + HEADER_ROW_LENGTH
      + beginningRows.length
      + tagGroupRows.length,
  });

  const grid = [
    ...sheetMetadata,
    headerRow,
    ...beginningRows,
    ...tagGroupRows,
    ...summaryRows,
  ];

  const metadata = etrStyleBuilder({
    sheetName: sheet.name,
    sheetMetadataLength: sheetMetadata.length,
    numberOfRowsToStick: numberOfAdditionalRowsToStick + sheetMetadata.length,
    numberOfColumnsToStick,
    dataColumnCount: data.columnHeaders.length,
    percentageRowsIndexes,
    nonCollapsedRowsIndexes,
    totalRowsIndexes,
    grid,
    hideDecimals,
    negativeMinus,
    shouldDisplayZero,
  });

  return {
    metadata,
    grid,
  };
};

export default etrBuilder;
